import {
  Box,
  Typography,
  FormControl,
  OutlinedInput,
  InputAdornment,
  Button,
  IconButton,
  Alert,
  Grid,
  Stack,
  useMediaQuery,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ButtonElement from "../UI/ButtonElement/ButtonElement";
import {
  ListCifCupsFormatted,
  ManagementUpdateCup,
} from "../../core/models/cupsCifSelector/cupsCifSelector.model";
import { RefObject, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./CupsCifSelector.scss";
import TotalEnergiesContext from "../../core/store/context/TotalEnergiesContext";
import {
  clearFirstLoadCifCups,
  newLoadCipCups,
} from "../../utils/components/cupsCifSelector/cupsCifSelector.util";
import CupsCifSelectorTables from "./CupsCifSelectorTables/CupsCifSelectorTables";
import LoadingComponentComponent from "../UI/LoadingComponentComponent/LoadingComponentComponent";
import EmptyStateComponent from "../UI/EmptyStateComponent/EmptyStateComponent";

export interface CupsCifSelectorProps {
  setUpdateCups: (ManagementUpdateCup: ManagementUpdateCup[]) => void;
  title: string;
  description: string;
  withContainer?: boolean;
  selectedCups: ManagementUpdateCup[];
  setSelectedCups: React.Dispatch<React.SetStateAction<ManagementUpdateCup[]>>;
  setScrollPosition?: React.Dispatch<React.SetStateAction<number | null>>;
  breakpoints?: { md: number; lg: number };
  isManagementsComponent?: boolean;
  tableElementRef?: RefObject<HTMLDivElement>;
  bgDark?: boolean;
}

const CupsCifSelector = ({
  setUpdateCups,
  title,
  withContainer,
  description,
  breakpoints,
  selectedCups,
  setSelectedCups,
  setScrollPosition,
  isManagementsComponent = true,
  tableElementRef,
  bgDark,
}: CupsCifSelectorProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cifCupsState, storageCifCupsState } =
    useContext(TotalEnergiesContext);
  const isDesktopViewport = useMediaQuery("(min-width:768px)");
  const [searchValue, setSearchValue] = useState<string>("");
  const [showAlertError, setShowAlertError] = useState<boolean>(false);
  const [resultsNumber, setResultsNumber] = useState<number>(0);
  const [blockButton, setblockButton] = useState<boolean>(true);
  const componentRef = useRef<HTMLDivElement>(null);
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (componentRef.current) {
        const component = componentRef.current;
        const componentRect = component.getBoundingClientRect();
        const isAtBottom = componentRect.bottom <= window.innerHeight;
        if (isAtBottom && !isDesktopViewport) {
          setblockButton(false);
        } else {
          setblockButton(true);
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isDesktopViewport]);

  useEffect(() => {
    if (!isManagementsComponent) {
      setUpdateCups(selectedCups);
    }
  }, [isManagementsComponent, selectedCups, setUpdateCups]);

  useEffect(() => {
    if (cifCupsState.page === 1) {
      tableElementRef?.current?.scrollTo({ top: 0, behavior: "auto" });
      setSearchValue(cifCupsState.searchedValue);
    }
  }, [cifCupsState, tableElementRef]);

  const handleClick = (value: string) => {
    let isCifSearch = false;

    const formattedValue = value.trim().toUpperCase();
    const filteredListCif = cifCupsState.listCupsCifRaw.filter(
      ({ cif, cups }) => {
        if (cif.includes(formattedValue)) {
          isCifSearch = true;
        }

        return (
          cif.includes(formattedValue) ||
          cups.some(({ cupsCode }) => cupsCode.includes(formattedValue))
        );
      }
    );

    const filterListCifCups: ListCifCupsFormatted[] = isCifSearch
      ? filteredListCif
      : filteredListCif.map((filteredList) => ({
          ...filteredList,
          cups: filteredList.cups.filter(({ cupsCode }) =>
            cupsCode.includes(formattedValue)
          ),
        }));

    setResultsNumber(
      formattedValue.length
        ? filterListCifCups.map(({ cups }) => cups).flat().length
        : 0
    );
    storageCifCupsState(
      clearFirstLoadCifCups(
        cifCupsState.listCupsCifRaw,
        filterListCifCups,
        value.trim()
      )
    );
    setShowAlertError(false);
  };

  const handleDelete = () => {
    storageCifCupsState(clearFirstLoadCifCups(cifCupsState.listCupsCifRaw));
    setShowAlertError(false);
    setSearchValue("");
  };

  const handleChangeCheckbox = (
    event: React.ChangeEvent<HTMLInputElement>,
    cup: ManagementUpdateCup
  ) => {
    if (event.target.checked && !isCupSelected(cup.cupsContractId)) {
      setSelectedCups((prevSelectedCups) => [...prevSelectedCups, cup]);
      return (
        setScrollPosition &&
        setScrollPosition(tableElementRef?.current?.scrollTop as number)
      );
    }

    setSelectedCups((preSelectedCups) =>
      preSelectedCups.filter(
        ({ cupsContractId }) => cupsContractId !== cup.cupsContractId
      )
    );
    setShowAlertError(false);
  };

  const handleChangeSelectAllChechbox = (
    event: React.ChangeEvent<HTMLInputElement>,
    cups: ManagementUpdateCup[],
    cif: string
  ) => {
    if (event.target.checked) {
      setSelectedCups((prevSelectedCups) => [...prevSelectedCups, ...cups]);
      setScrollPosition &&
        setScrollPosition(tableElementRef?.current?.scrollTop as number);
      return;
    }

    setSelectedCups((prevSelectedCups) =>
      prevSelectedCups.filter(
        ({ cupsAssociatedCif }) => cif !== cupsAssociatedCif
      )
    );

    setShowAlertError(false);
  };

  const lastElementRef = (divElement: HTMLDivElement) => {
    if (observer.current) {
      observer.current.disconnect();
    }

    observer.current = new IntersectionObserver((entries) => {
      if (
        entries[0].isIntersecting &&
        cifCupsState.page < cifCupsState.totalPages
      ) {
        storageCifCupsState(newLoadCipCups(cifCupsState));
      }
    });

    if (divElement) {
      observer.current.observe(divElement);
    }
  };

  const isCupSelected = (id: string) =>
    selectedCups.findIndex(({ cupsContractId }) => cupsContractId === id) !==
    -1;

  return (
    <Grid ref={componentRef} justifyContent="center" container={withContainer}>
      <Grid item md={breakpoints?.md || 8} lg={breakpoints?.lg || 7}>
        <Box
          component="div"
          boxShadow={bgDark ? "none" : ""}
          className={"managements-cups-cif-selector"}
        >
          <Stack
            component="div"
            spacing={3}
            className="managements-cups-cif-selector__header"
          >
            <Typography component="h3" variant="h3">
              {t(title)}
            </Typography>
            <Typography component="p" variant="body1">
              {t(description)}
            </Typography>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                handleClick(searchValue);
              }}
              className="managements-cups-cif-selector__search"
            >
              <FormControl
                variant={"outlined"}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  width:
                    !isManagementsComponent && isDesktopViewport
                      ? "60%"
                      : "auto",
                }}
              >
                <OutlinedInput
                  placeholder={t("Gestiones.Buscar")}
                  value={searchValue}
                  className="form-control"
                  startAdornment={
                    <InputAdornment position="start" className="search-icon">
                      <span className="t-icon-search" aria-hidden="true"></span>
                    </InputAdornment>
                  }
                  endAdornment={
                    <InputAdornment position="end">
                      {!!searchValue.length && (
                        <IconButton size="small" onClick={() => handleDelete()}>
                          <span className="t-icon-close" />
                        </IconButton>
                      )}
                    </InputAdornment>
                  }
                  onChange={(event) => {
                    setSearchValue(event.target.value);
                  }}
                />
                <Button
                  variant="contained"
                  type="submit"
                  className="btn-submit"
                  disabled={cifCupsState.isLoading}
                >
                  {t("Gestiones.Buscar")}
                </Button>
              </FormControl>
            </form>
            {showAlertError && (
              <Alert severity="error" variant="outlined">
                {t("Sólo puedes seleccionar un titular por gestión")}
              </Alert>
            )}
            {!!cifCupsState.searchedValue.length && (
              <Typography component="p" variant="body1">
                {`${resultsNumber} ${t(
                  `Gestiones.${
                    resultsNumber === 1 ? "Resultado" : "Resultados"
                  }`
                )} "${cifCupsState.searchedValue}"`}
              </Typography>
            )}
          </Stack>
          {cifCupsState.isLoading ? (
            <Box width={"100%"} height={"500px"}>
              <LoadingComponentComponent
                isLoading={cifCupsState.isLoading}
                position="relative"
              />
            </Box>
          ) : (
            <>
              {!!cifCupsState.listCupsCifFiltered.length ? (
                <CupsCifSelectorTables
                  isManagementsComponent={isManagementsComponent}
                  isCupSelected={isCupSelected}
                  listCupsCifFiltered={cifCupsState.listCupsCifFiltered}
                  selectedCups={selectedCups}
                  handleChangeCheckbox={handleChangeCheckbox}
                  handleChangeSelectAllChechbox={handleChangeSelectAllChechbox}
                  searchedValue={cifCupsState.searchedValue}
                  setShowAlertError={setShowAlertError}
                  lastElementRef={lastElementRef}
                  page={cifCupsState.page}
                  tableElementRef={tableElementRef}
                  bgDark={bgDark}
                />
              ) : (
                <EmptyStateComponent
                  text={"No hay resultados"}
                  height="500px"
                />
              )}
            </>
          )}
          {isManagementsComponent && (
            <Stack
              component="div"
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              className={`managements-cups-cif-selector__footer ${
                blockButton ? "" : "block-btn"
              }`}
            >
              <ButtonElement
                onClick={() => {
                  setUpdateCups(selectedCups);
                }}
                disabled={!selectedCups.length && isDesktopViewport}
              >
                {t("Gestiones.Seleccionar CIF / CUPS")}
              </ButtonElement>

              {isDesktopViewport && (
                <ButtonElement
                  onClick={() => {
                    navigate(process.env.REACT_APP_MANAGEMENT!);
                  }}
                  secondary
                >
                  {t("Cancelar")}
                </ButtonElement>
              )}
            </Stack>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default CupsCifSelector;
